import { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import parse from "html-react-parser";
import { 
  ChevronLeft, 
  ShieldCheck, 
  Clock, 
  Layers, 
  PlayCircle, 
  FileText, 
  Star,
  Users,
  CheckCircle2,
  Trophy,
  Share2,
  Info,
  ArrowRight,
  BookOpen,
  Sparkles
} from "lucide-react";
import { fetchEnrollmentStatus } from "../../../services/apiEndpoints";
import { useToast } from "../../../hooks/otherHooks/useToast";
import { useCart } from "../../../hooks/useCart";
import Button from "../../../components/common/Button";
import {
  useCourseDetailQuery,
  useEnrollmentStatusQuery,
} from "../../../hooks/queries/useCourseQueries";

const StudentCourseDetail = () => {
  const location = useLocation();
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { addToCart, checkInCart } = useCart();

  const isTrack = location.pathname.toLowerCase().includes("track");
  const { data: detail = null, isLoading: loading } = useCourseDetailQuery(slug, isTrack);

  const { data: enrollmentStatus } = useEnrollmentStatusQuery(
    {
      courseId: isTrack ? undefined : detail?.id,
      trackId: isTrack ? detail?.id : undefined,
    },
    { enabled: Boolean(detail?.id) }
  );

  const isEnrolled = Boolean(enrollmentStatus?.enrolled);
  const [enrollloading, setEnrollLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("curriculum");

  useEffect(() => {
    if (detail) {
      // Auto-select first available tab
      const available = [
        {
          id: "curriculum",
          show: isTrack
            ? detail?.course_in_track?.length > 0
            : Boolean(detail?.outline || detail?.modules?.length > 0),
        },
        { id: "instructor", show: Boolean(detail?.instructor) },
        {
          id: "reviews",
          show: isTrack
            ? detail?.track_review?.length > 0
            : detail?.course_review?.length > 0,
        },
      ].filter((t) => t.show);

      if (available.length > 0) {
        setActiveTab(available[0].id);
      }
    }
  }, [detail, isTrack]);

  const handleFetchEnrollmentStatus = async () => {
    if (!detail) return;
    setEnrollLoading(true);
    try {
      const response = await fetchEnrollmentStatus(
        isTrack ? { trackId: detail.id } : { courseId: detail.id }
      );

      if (response.enrolled && response.paid) {
        navigate(
          isTrack
            ? `/student/dashboard/track/${slug}/enrolled?trackId=${detail.id}`
            : `/student/dashboard/courses/${slug}/enrolled?courseId=${detail.id}`
        );
      } else if (!response.paid) {
        addToast({ message: "Payment required. Please check your cart.", bg: "danger" });
      }
    } catch (error) {
      addToast({ message: "Unable to verify enrollment.", bg: "warning" });
    } finally {
      setEnrollLoading(false);
    }
  };

  const handleAddToCart = () => {
    if (detail) {
      addToCart(detail.id, isTrack ? "coursetrack" : "course", "online", detail.title);
    }
  };

  const isInCart = detail ? checkInCart(detail.id, isTrack ? "coursetrack" : "course", "online") : false;

  // Compute total lessons & tasks dynamically
  const totalLessons = isTrack
    ? (detail?.course_in_track?.reduce(
        (sum: number, c: any) =>
          sum +
          (c.modules?.reduce(
            (mSum: number, m: any) =>
              mSum + (m.lessons?.length || 0) + (m.exercises?.length || m.exercise_module?.length || 0),
            0
          ) || Number(c.lessons_count) || 0),
        0
      ) || Number(detail?.lessons_count) || 0)
    : (detail?.modules?.reduce(
        (sum: number, m: any) =>
          sum + (m.lessons?.length || 0) + (m.exercises?.length || m.exercise_module?.length || 0),
        0
      ) || Number(detail?.lessons_count) || 0);

  const displayPrice = detail?.payment_type === "Free"
    ? "Free"
    : `₦${Number(detail?.online_price || 0).toLocaleString()}`;

  if (loading) return (
    <div className="cht-flex cht-items-center cht-justify-center cht-h-[60vh]">
      <div className="cht-w-10 cht-h-10 cht-border-4 cht-border-[#3e57da]/20 cht-border-t-[#3e57da] cht-rounded-full cht-animate-spin"></div>
    </div>
  );

  return (
    <div className="cht-animate-in cht-fade-in cht-duration-700 cht-space-y-8">
      
      {/* Breadcrumb Navigation */}
      <nav className="cht-flex cht-items-center cht-gap-2.5 cht-text-xs cht-font-semibold cht-text-slate-400">
        <Link to="/student/dashboard/courses" className="hover:cht-text-[#3e57da] cht-transition-colors cht-no-underline">
          Courses
        </Link>
        <ChevronLeft size={13} className="cht-rotate-180 cht-text-slate-300" />
        <span className="cht-text-slate-600 cht-font-bold">
          {isTrack ? "Career Track" : "Individual Course"}
        </span>
        <ChevronLeft size={13} className="cht-rotate-180 cht-text-slate-300" />
        <span className="cht-text-slate-900 cht-font-bold cht-truncate cht-max-w-xs sm:cht-max-w-md">
          {detail?.title}
        </span>
      </nav>

      <div className="cht-grid cht-grid-cols-1 lg:cht-grid-cols-12 cht-gap-8 lg:cht-gap-10 cht-items-start">
        
        {/* Main Content Area (8 cols) */}
        <div className="lg:cht-col-span-8 cht-space-y-8">
          
          {/* Elevated Hero Card */}
          <section className="cht-bg-white cht-rounded-[28px] cht-p-6 sm:cht-p-8 lg:cht-p-10 cht-border cht-border-slate-200/80 cht-shadow-sm cht-space-y-6">
            
            {/* Top Badges */}
            <div className="cht-flex cht-flex-wrap cht-items-center cht-gap-2.5">
              <span className="cht-inline-flex cht-items-center cht-gap-1.5 cht-px-3.5 cht-py-1.5 cht-bg-indigo-50 cht-border cht-border-indigo-100 cht-rounded-full cht-text-xs !cht-font-body cht-font-bold cht-text-[#3e57da]">
                <ShieldCheck size={14} className="cht-text-[#3e57da]" />
                {isTrack ? "Comprehensive Career Track" : "Verified Curriculum"}
              </span>
              {detail?.level && (
                <span className="cht-inline-flex cht-items-center cht-px-3 cht-py-1.5 cht-bg-slate-100 cht-rounded-full cht-text-xs !cht-font-body cht-font-bold cht-text-slate-600">
                  {detail.level}
                </span>
              )}
            </div>

            {/* Title */}
            <h1 className="!cht-font-body cht-text-3xl sm:cht-text-4xl lg:cht-text-5xl !cht-font-extrabold cht-text-slate-900 cht-tracking-tight !cht-leading-[1.15]">
              {detail?.title}
            </h1>

            {/* Quick Metrics Bar */}
            <div className="cht-flex cht-flex-wrap cht-items-center cht-gap-3 cht-pt-1">
              {/* Rating */}
              <div className="cht-inline-flex cht-items-center cht-gap-1.5 cht-px-3 cht-py-1.5 cht-bg-amber-50/80 cht-border cht-border-amber-200/60 cht-rounded-xl cht-text-xs !cht-font-body cht-font-bold cht-text-amber-800">
                <Star size={14} className="cht-text-amber-500 fill-amber-500" />
                <span>{Number(detail?.rating) > 0 ? Number(detail.rating).toFixed(1) : "0.0"}</span>
                <span className="cht-text-amber-700/70 cht-font-medium">({detail?.review_count ?? 0} reviews)</span>
              </div>

              {/* Enrolled */}
              <div className="cht-inline-flex cht-items-center cht-gap-1.5 cht-px-3 cht-py-1.5 cht-bg-indigo-50/80 cht-border cht-border-indigo-100 cht-rounded-xl cht-text-xs !cht-font-body cht-font-bold cht-text-indigo-800">
                <Users size={14} className="cht-text-[#3e57da]" />
                <span>{detail?.enrolled_count ?? 0} enrolled</span>
              </div>

              {/* Duration */}
              {detail?.duration && (
                <div className="cht-inline-flex cht-items-center cht-gap-1.5 cht-px-3 cht-py-1.5 cht-bg-slate-100/80 cht-border cht-border-slate-200/80 cht-rounded-xl cht-text-xs !cht-font-body cht-font-bold cht-text-slate-700">
                  <Clock size={14} className="cht-text-slate-500" />
                  <span>{detail.duration}</span>
                </div>
              )}

              {/* Course count if Track */}
              {isTrack && detail?.course_in_track?.length > 0 && (
                <div className="cht-inline-flex cht-items-center cht-gap-1.5 cht-px-3 cht-py-1.5 cht-bg-purple-50 cht-border cht-border-purple-100 cht-rounded-xl cht-text-xs !cht-font-body cht-font-bold cht-text-purple-700">
                  <Layers size={14} className="cht-text-purple-600" />
                  <span>{detail.course_in_track.length} Integrated Courses</span>
                </div>
              )}
            </div>

            {/* Overview / Description */}
            <div className="cht-pt-4 cht-border-t cht-border-slate-100 cht-space-y-2">
              <p className="cht-text-[11px] !cht-font-body cht-font-black cht-text-slate-400 cht-uppercase cht-tracking-wider">
                Program Overview
              </p>
              <div className="!cht-font-body cht-text-slate-600 cht-leading-relaxed cht-text-sm sm:cht-text-base">
                {detail?.description ? parse(detail.description) : "Learn industry-grade skills and advance your career with this curated curriculum."}
              </div>
            </div>
          </section>

          {/* Tab Navigation */}
          <div className="cht-flex cht-items-center cht-gap-6 sm:cht-gap-8 cht-border-b cht-border-slate-200 cht-pb-1">
            {[
              { 
                id: "curriculum", 
                label: isTrack
                  ? `Curriculum (${detail?.course_in_track?.length || 0})`
                  : detail?.modules?.length > 0
                  ? `Syllabus (${detail.modules.length} Modules)`
                  : "Syllabus", 
                show: isTrack
                  ? detail?.course_in_track?.length > 0
                  : Boolean(detail?.outline || detail?.modules?.length > 0)
              },
              { 
                id: "instructor", 
                label: "Lead Instructor", 
                show: !!detail?.instructor 
              },
              { 
                id: "reviews", 
                label: `Reviews (${(isTrack ? detail?.track_review : detail?.course_review)?.length || 0})`, 
                show: isTrack ? detail?.track_review?.length > 0 : detail?.course_review?.length > 0 
              }
            ].filter(t => t.show).map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`cht-pb-3 cht-text-xs !cht-font-body cht-font-bold cht-tracking-wide cht-transition-all cht-relative cht-outline-none ${
                  activeTab === tab.id 
                    ? "cht-text-[#3e57da]" 
                    : "cht-text-slate-400 hover:cht-text-slate-700"
                }`}
              >
                <span>{tab.label}</span>
                {activeTab === tab.id && (
                  <motion.div 
                    layoutId="activeTabUnderline"
                    className="cht-absolute cht-bottom-0 cht-left-0 cht-right-0 cht-h-0.5 cht-bg-[#3e57da] cht-rounded-full"
                    transition={{ type: "spring", stiffness: 400, damping: 35 }}
                  />
                )}
              </button>
            ))}
          </div>

          {/* Tab Contents Card */}
          <div className="cht-bg-white cht-rounded-[28px] cht-p-6 sm:cht-p-8 cht-border cht-border-slate-200/80 cht-shadow-sm">
            {activeTab === "curriculum" && (
              <div className="cht-space-y-6">
                <div>
                  <h3 className="!cht-font-body cht-text-xl !cht-font-bold cht-text-slate-900">
                    {isTrack ? "Learning Path Roadmap" : "Course Outline & Syllabus"}
                  </h3>
                  <p className="cht-text-xs cht-text-slate-500 cht-mt-1">
                    {isTrack 
                      ? "Complete each course phase sequentially to master the entire career track."
                      : "Detailed breakdown of the topics, lessons, and hands-on exercises covered in this course."
                    }
                  </p>
                </div>
                
                {isTrack ? (
                  /* Track Roadmap Step Timeline */
                  <div className="cht-relative cht-space-y-4 cht-pt-2">
                    {detail?.course_in_track?.map((course: any, idx: number) => {
                      const courseLessons =
                        course.modules?.reduce(
                          (acc: number, m: any) =>
                            acc +
                            (m.lessons?.length || 0) +
                            (m.exercises?.length || m.exercise_module?.length || 0),
                          0
                        ) || Number(course.lessons_count) || 0;
                      const isLast = idx === (detail.course_in_track.length - 1);

                      return (
                        <div key={course.id || idx} className="cht-relative cht-flex cht-items-start cht-gap-4 sm:cht-gap-5">
                          
                          {/* Timeline vertical node */}
                          <div className="cht-flex cht-flex-col cht-items-center cht-self-stretch cht-shrink-0 cht-pt-1">
                            <div className="cht-w-9 cht-h-9 cht-rounded-xl cht-bg-indigo-50 cht-border-2 cht-border-[#3e57da] cht-flex cht-items-center cht-justify-center cht-text-xs !cht-font-body !cht-font-black cht-text-[#3e57da] cht-shadow-sm">
                              0{idx + 1}
                            </div>
                            {!isLast && (
                              <div className="cht-w-0.5 cht-bg-indigo-100 cht-flex-1 cht-my-2 cht-min-h-[40px]"></div>
                            )}
                          </div>

                          {/* Phase Course Card */}
                          <Link
                            to={course.slug ? `/student/dashboard/courses/${course.slug}` : "#"}
                            className="cht-flex-1 cht-bg-slate-50/60 hover:cht-bg-white cht-border cht-border-slate-200/80 hover:cht-border-[#3e57da]/50 cht-rounded-2xl cht-p-5 sm:cht-p-6 cht-shadow-[0_2px_8px_rgba(0,0,0,0.02)] hover:cht-shadow-md cht-transition-all cht-no-underline cht-group cht-block cht-mb-2"
                          >
                            <div className="cht-flex cht-flex-col sm:cht-flex-row sm:cht-items-center sm:cht-justify-between cht-gap-4">
                              <div className="cht-space-y-2">
                                <div className="cht-flex cht-items-center cht-gap-2">
                                  <span className="cht-px-2.5 cht-py-0.5 cht-bg-white cht-border cht-border-slate-200 cht-text-slate-600 cht-rounded-md cht-text-[10px] !cht-font-body cht-font-bold cht-uppercase cht-tracking-wider">
                                    Phase 0{idx + 1} • Course
                                  </span>
                                  {course.level && (
                                    <span className="cht-px-2 cht-py-0.5 cht-bg-indigo-50 cht-text-[#3e57da] cht-rounded-md cht-text-[10px] !cht-font-body cht-font-semibold">
                                      {course.level}
                                    </span>
                                  )}
                                </div>
                                
                                <h4 className="!cht-font-body cht-text-base sm:cht-text-lg !cht-font-bold cht-text-slate-900 group-hover:cht-text-[#3e57da] cht-transition-colors">
                                  {course.title}
                                </h4>

                                <div className="cht-flex cht-flex-wrap cht-items-center cht-gap-4 cht-text-xs cht-text-slate-500 cht-font-medium">
                                  {course.duration && (
                                    <span className="cht-flex cht-items-center cht-gap-1.5">
                                      <Clock size={13} className="cht-text-slate-400" />
                                      {course.duration}
                                    </span>
                                  )}
                                  {courseLessons > 0 && (
                                    <span className="cht-flex cht-items-center cht-gap-1.5">
                                      <PlayCircle size={13} className="cht-text-slate-400" />
                                      {courseLessons} Lessons & Tasks
                                    </span>
                                  )}
                                  {course.modules?.length > 0 && (
                                    <span className="cht-flex cht-items-center cht-gap-1.5">
                                      <Layers size={13} className="cht-text-slate-400" />
                                      {course.modules.length} Modules
                                    </span>
                                  )}
                                </div>
                              </div>

                              <div className="cht-flex cht-items-center cht-gap-2.5 cht-self-end sm:cht-self-center cht-shrink-0">
                                <span className="cht-hidden sm:cht-inline cht-text-xs !cht-font-body cht-font-bold cht-text-[#3e57da] group-hover:cht-underline">
                                  View Syllabus
                                </span>
                                <div className="cht-w-9 cht-h-9 cht-rounded-xl cht-bg-white group-hover:cht-bg-[#3e57da] cht-border cht-border-slate-200 group-hover:cht-border-transparent cht-flex cht-items-center cht-justify-center cht-text-slate-400 group-hover:cht-text-white cht-transition-all cht-shadow-sm">
                                  <ArrowRight size={16} />
                                </div>
                              </div>
                            </div>
                          </Link>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  /* Course Syllabus Preview */
                  <div className="cht-space-y-6">
                    {detail?.modules?.length > 0 && (
                      <div className="cht-space-y-3">
                        {detail.modules.map((mod: any, idx: number) => {
                          const lessons = mod.lessons || [];
                          const exercises = mod.exercises || mod.exercise_module || [];
                          return (
                            <div
                              key={mod.id || idx}
                              className="cht-p-4 sm:cht-p-5 cht-bg-slate-50/70 cht-rounded-2xl cht-border cht-border-slate-200/80 cht-space-y-3"
                            >
                              <div className="cht-flex cht-flex-wrap cht-items-center cht-justify-between cht-gap-2">
                                <div className="cht-flex cht-items-center cht-gap-2.5">
                                  <span className="cht-px-2.5 cht-py-1 cht-bg-indigo-50 cht-text-[#3e57da] cht-rounded-lg cht-text-[11px] !cht-font-body cht-font-black">
                                    Module {idx + 1}
                                  </span>
                                  <h4 className="!cht-font-body cht-text-sm sm:cht-text-base !cht-font-bold cht-text-slate-900">
                                    {mod.title}
                                  </h4>
                                </div>
                                <div className="cht-flex cht-items-center cht-gap-3 cht-text-xs cht-font-semibold cht-text-slate-500">
                                  <span>{lessons.length} {lessons.length === 1 ? "Lesson" : "Lessons"}</span>
                                  {exercises.length > 0 && (
                                    <span>• {exercises.length} {exercises.length === 1 ? "Task" : "Tasks"}</span>
                                  )}
                                </div>
                              </div>
                              {lessons.length > 0 && (
                                <ul className="cht-space-y-1.5 cht-pt-2 cht-border-t cht-border-slate-200/60">
                                  {lessons.map((lesson: any, lIdx: number) => (
                                    <li
                                      key={lesson.id || lIdx}
                                      className="cht-flex cht-items-center cht-justify-between cht-text-xs cht-text-slate-600 cht-py-1"
                                    >
                                      <span className="cht-flex cht-items-center cht-gap-2 cht-font-medium">
                                        <PlayCircle size={13} className="cht-text-[#3e57da] cht-shrink-0" />
                                        {lesson.title}
                                      </span>
                                      {lesson.duration && (
                                        <span className="cht-text-[11px] cht-text-slate-400 cht-font-semibold">
                                          {lesson.duration}
                                        </span>
                                      )}
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {detail?.outline && (
                      <div className="cht-prose cht-max-w-none cht-text-slate-600 cht-leading-relaxed">
                        {parse(detail.outline)}
                      </div>
                    )}
                    {!detail?.outline && (!detail?.modules || detail.modules.length === 0) && (
                      <p className="cht-text-sm cht-text-slate-500">Detailed outline coming soon...</p>
                    )}
                  </div>
                )}
              </div>
            )}
            
            {activeTab === "instructor" && (
              <div className="cht-flex cht-flex-col md:cht-flex-row cht-items-center md:cht-items-start cht-gap-8">
                <div className="cht-w-28 cht-h-28 sm:cht-w-32 sm:cht-h-32 cht-rounded-2xl cht-overflow-hidden cht-shadow-md cht-border cht-border-slate-100 cht-shrink-0">
                  <img 
                    src={detail?.instructor?.get_photo_url || "https://cdn-icons-png.flaticon.com/512/147/147142.png"} 
                    className="cht-w-full cht-h-full cht-object-cover" 
                    alt="instructor" 
                  />
                </div>
                <div className="cht-space-y-4 cht-flex-1">
                  <div>
                    <h4 className="!cht-font-body cht-text-2xl !cht-font-bold cht-text-slate-900">
                      {detail?.instructor?.name || "Lead Instructor"}
                    </h4>
                    <p className="cht-text-xs cht-text-[#3e57da] cht-font-semibold cht-mt-0.5">
                      Senior Curriculum Director & Mentor
                    </p>
                  </div>
                  <div className="cht-text-sm cht-text-slate-600 cht-leading-relaxed">
                    {detail?.instructor?.bio
                      ? parse(detail.instructor.bio)
                      : "Industry veteran with extensive production experience leading software engineering and digital transformation projects. Dedicated to practical, career-ready education."}
                  </div>
                  <div className="cht-flex cht-items-center cht-gap-6 cht-pt-2">
                    <div>
                      <p className="!cht-font-body cht-text-lg !cht-font-bold cht-text-slate-900">
                        {detail?.instructor?.students_count ?? 0}
                      </p>
                      <p className="cht-text-[11px] cht-font-semibold cht-text-slate-400 cht-uppercase">Students</p>
                    </div>
                    <div className="cht-w-px cht-h-8 cht-bg-slate-200"></div>
                    <div>
                      <p className="!cht-font-body cht-text-lg !cht-font-bold cht-text-slate-900">
                        {Number(detail?.instructor?.rating) > 0
                          ? Number(detail.instructor.rating).toFixed(1)
                          : "0.0"}
                      </p>
                      <p className="cht-text-[11px] cht-font-semibold cht-text-slate-400 cht-uppercase">Rating</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "reviews" && (
              <div className="cht-space-y-6">
                <div className="cht-flex cht-items-center cht-justify-between cht-border-b cht-border-slate-100 cht-pb-4">
                  <div>
                    <h3 className="!cht-font-body cht-text-lg !cht-font-bold cht-text-slate-900">Student Reviews & Feedback</h3>
                    <p className="cht-text-xs cht-text-slate-500">Real feedback from students who completed this curriculum</p>
                  </div>
                  <div className="cht-flex cht-items-center cht-gap-2 cht-bg-amber-50 cht-px-3.5 cht-py-1.5 cht-rounded-xl cht-border cht-border-amber-200/60">
                    <Star className="cht-text-amber-500 fill-amber-500" size={16} />
                    <span className="!cht-font-body cht-text-xl !cht-font-black cht-text-amber-900">
                      {Number(detail?.rating) > 0 ? Number(detail.rating).toFixed(1) : "0.0"}
                    </span>
                  </div>
                </div>
                
                <div className="cht-grid md:cht-grid-cols-2 cht-gap-5">
                  {(isTrack ? detail?.track_review : detail?.course_review)?.map((review: any, idx: number) => (
                    <div key={idx} className="cht-p-5 cht-bg-slate-50/70 cht-rounded-2xl cht-border cht-border-slate-200/80 cht-space-y-3">
                      <div className="cht-flex cht-items-center cht-gap-3">
                        <div className="cht-w-10 cht-h-10 cht-rounded-full cht-overflow-hidden cht-border cht-border-slate-200">
                          <img src={review.get_image_url || "https://cdn-icons-png.flaticon.com/512/147/147142.png"} alt="student" className="cht-w-full cht-h-full cht-object-cover" />
                        </div>
                        <div>
                          <p className="!cht-font-body cht-text-sm !cht-font-bold cht-text-slate-900">{review.name}</p>
                          <p className="cht-text-[11px] cht-text-slate-400">{review.position || "Student"}</p>
                        </div>
                      </div>
                      <div className="cht-text-xs sm:cht-text-sm cht-text-slate-600 cht-leading-relaxed cht-italic">
                        "{parse(review.message || "")}"
                      </div>
                      <div className="cht-flex cht-gap-1">
                        {[...Array(review.rating || 5)].map((_, i) => (
                          <Star key={i} size={12} className="cht-text-amber-400 fill-amber-400" />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

        </div>

        {/* Sidebar Enrollment Card (4 cols) */}
        <div className="lg:cht-col-span-4">
          <div className="cht-sticky cht-top-24 cht-p-6 sm:cht-p-8 cht-bg-white cht-border cht-border-slate-200/80 cht-rounded-[32px] cht-shadow-[0_10px_30px_rgba(0,0,0,0.04)] cht-space-y-6">
            
            {/* Price Header */}
            <div className="cht-space-y-1">
              <span className="cht-text-[11px] !cht-font-body cht-font-black cht-uppercase cht-tracking-wider cht-text-[#3e57da]">
                Full Lifetime Access
              </span>
              <h3 className="!cht-font-body cht-text-4xl !cht-font-black cht-text-slate-900 cht-tracking-tight">
                {displayPrice}
              </h3>
            </div>

            {/* Action Buttons */}
            <div className="cht-space-y-3">
              {isEnrolled ? (
                <button 
                  onClick={handleFetchEnrollmentStatus}
                  disabled={enrollloading}
                  className="cht-w-full cht-py-3.5 cht-px-6 cht-bg-[#3e57da] hover:cht-bg-[#3346b8] cht-text-white !cht-font-body cht-font-bold cht-text-sm cht-rounded-2xl cht-shadow-md hover:cht-shadow-lg cht-transition-all cht-flex cht-items-center cht-justify-center cht-gap-2"
                >
                  {enrollloading ? "Verifying..." : "Continue Learning"}
                  <ArrowRight size={16} />
                </button>
              ) : (
                <>
                  <button 
                    onClick={handleAddToCart}
                    disabled={isInCart}
                    className={`cht-w-full cht-py-3.5 cht-px-6 !cht-font-body cht-font-bold cht-text-sm cht-rounded-2xl cht-transition-all cht-flex cht-items-center cht-justify-center cht-gap-2 ${
                      isInCart 
                        ? "cht-bg-emerald-50 cht-border cht-border-emerald-200 cht-text-emerald-700"
                        : "cht-bg-[#3e57da] hover:cht-bg-[#3346b8] cht-text-white cht-shadow-md hover:cht-shadow-lg"
                    }`}
                  >
                    {isInCart ? (
                      <>
                        <CheckCircle2 size={16} className="cht-text-emerald-600" />
                        In Your Cart
                      </>
                    ) : (
                      <>
                        <Sparkles size={16} />
                        Enroll in {isTrack ? "Career Track" : "Course"}
                      </>
                    )}
                  </button>
                  <button 
                    onClick={() => {
                      if (navigator.share) {
                        navigator.share({ title: detail?.title, url: window.location.href });
                      } else {
                        navigator.clipboard.writeText(window.location.href);
                        addToast({ message: "Link copied to clipboard!", bg: "success" });
                      }
                    }}
                    className="cht-w-full cht-py-2.5 cht-px-4 cht-bg-slate-100 hover:cht-bg-slate-200/80 cht-text-slate-700 !cht-font-body cht-font-bold cht-text-xs cht-rounded-xl cht-transition-colors cht-flex cht-items-center cht-justify-center cht-gap-2"
                  >
                    <Share2 size={14} />
                    Share Program
                  </button>
                </>
              )}
            </div>

            {/* What's Included Feature List */}
            <div className="cht-space-y-4 cht-pt-2 cht-border-t cht-border-slate-100">
              <p className="cht-text-[11px] !cht-font-body cht-font-black cht-text-slate-400 cht-uppercase cht-tracking-wider">
                What's included in this {isTrack ? "Track" : "Course"}
              </p>
              <ul className="cht-space-y-3.5">
                {[
                  { 
                    icon: PlayCircle, 
                    label: totalLessons > 0 ? `${totalLessons}+ High-res lessons` : `${isTrack ? detail?.course_in_track?.length || 0 : 1} Structured Courses`, 
                    show: true 
                  },
                  { 
                    icon: Layers, 
                    label: isTrack ? `${detail?.course_in_track?.length || 0} Integrated Courses with Milestone Progression` : "Comprehensive Modules & Quizzes", 
                    show: isTrack 
                  },
                  { 
                    icon: FileText, 
                    label: "Source code & Digital assets", 
                    show: detail?.has_digital_assets ?? true 
                  },
                  { 
                    icon: Trophy, 
                    label: "Verified Certificate of Completion", 
                    show: detail?.has_certificate ?? true 
                  },
                  { 
                    icon: ShieldCheck, 
                    label: "Full Lifetime access & updates", 
                    show: detail?.has_lifetime_access ?? true 
                  },
                  { 
                    icon: Info, 
                    label: "Direct Mentor Q&A Support", 
                    show: detail?.has_mentor_support ?? true 
                  }
                ].filter(item => item.show).map((item, i) => (
                  <li key={i} className="cht-flex cht-items-center cht-gap-3 cht-text-xs sm:cht-text-sm !cht-font-body cht-font-semibold cht-text-slate-700">
                    <div className="cht-w-7 cht-h-7 cht-rounded-lg cht-bg-indigo-50 cht-flex cht-items-center cht-justify-center cht-text-[#3e57da] cht-shrink-0">
                      <item.icon size={15} />
                    </div>
                    <span>{item.label}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Money-Back Guarantee Trust Card */}
            <div className="cht-p-4 cht-bg-amber-50/70 cht-border cht-border-amber-200/60 cht-rounded-2xl cht-flex cht-items-center cht-gap-3">
              <div className="cht-w-9 cht-h-9 cht-bg-amber-100 cht-rounded-xl cht-flex cht-items-center cht-justify-center cht-shrink-0">
                <ShieldCheck size={18} className="cht-text-amber-700" />
              </div>
              <p className="cht-text-xs cht-font-medium cht-text-amber-900 leading-snug">
                <strong className="cht-font-bold">7-Day Satisfaction Guarantee:</strong> Full refund if you're not completely satisfied.
              </p>
            </div>
          </div>
        </div>

      </div>

    </div>
  );
};

export default StudentCourseDetail;

